import React, { useState, useCallback } from "react"
import PropTypes from "prop-types"
import { Checkbox } from "../ui/checkbox"
import { RadioGroup, RadioGroupItem } from "../ui/radio-group"
import { TextField } from "../form-fields/text-field/text-field"
import { SegmentedToggle } from "../form-fields/segmented-toggle/segmented-toggle"
import { cn } from "../../lib/utils"
import { AlertTriangle } from "lucide-react"

// Individual question component
const QuestionRenderer = ({ 
  question, 
  answers, 
  onAnswerChange, 
  readOnly = false,
  nestingLevel = 0,
  demographicErrorMessages = {}
}) => {
  const [localAnswer, setLocalAnswer] = useState(answers[question.questionId] || {
    questionId: question.questionId,
    answer: question.answer || null,
    selectedOptions: question.selectedOptions || []
  })

  const handleAnswerChange = useCallback((newAnswer) => {
    setLocalAnswer(newAnswer)
    onAnswerChange(newAnswer)
  }, [question.questionId, onAnswerChange])

  // Function to recursively clear all nested target questions
  const clearNestedTargetQuestions = useCallback((targetQuestions) => {
    if (!targetQuestions || targetQuestions.length === 0) return

    targetQuestions.forEach(targetQuestion => {
      // Clear the target question's answer
      const clearedAnswer = {
        questionId: targetQuestion.questionId,
        answer: null,
        selectedOptions: []
      }
      onAnswerChange(clearedAnswer)

      // If the target question has options with their own target questions, clear those too
      if (targetQuestion.options && targetQuestion.options.length > 0) {
        targetQuestion.options.forEach(option => {
          if (option.targetQuestions && option.targetQuestions.length > 0) {
            clearNestedTargetQuestions(option.targetQuestions)
          }
        })
      }
    })
  }, [onAnswerChange])

  const handleSingleSelect = (optionValue) => {
    const optionId = parseInt(optionValue)
    
    // Find the previously selected option to clear its target questions
    const previouslySelectedOptionId = localAnswer.selectedOptions?.[0]
    if (previouslySelectedOptionId && previouslySelectedOptionId !== optionId) {
      const previousOption = question.options.find(opt => opt.optionId === previouslySelectedOptionId)
      if (previousOption && previousOption.targetQuestions) {
        clearNestedTargetQuestions(previousOption.targetQuestions)
      }
    }

    const newAnswer = {
      ...localAnswer,
      selectedOptions: [optionId],
      answer: null
    }
    handleAnswerChange(newAnswer)
  }

  const handleMultiSelect = (optionId, checked) => {
    let newSelectedOptions = [...(localAnswer.selectedOptions || [])]
    
    if (checked) {
      if (!newSelectedOptions.includes(optionId)) {
        newSelectedOptions.push(optionId)
      }
    } else {
      newSelectedOptions = newSelectedOptions.filter(id => id !== optionId)
      
      // Clear target questions for the deselected option
      const deselectedOption = question.options.find(opt => opt.optionId === optionId)
      if (deselectedOption && deselectedOption.targetQuestions) {
        clearNestedTargetQuestions(deselectedOption.targetQuestions)
      }
    }

    const newAnswer = {
      ...localAnswer,
      selectedOptions: newSelectedOptions,
      answer: null
    }
    handleAnswerChange(newAnswer)
  }

  const handleMixedSelect = (optionId, checked) => {
    const option = question.options.find(opt => opt.optionId === optionId)
    const buttonType = option?.metaData?.buttonType
    
    let newSelectedOptions = [...(localAnswer.selectedOptions || [])]
    let optionsToDeselect = []
    
    if (buttonType === "Single Select") {
      if (checked) {
        // For Single Select options, clear all other selections and select only this one
        // Store previously selected options to clear their target questions
        optionsToDeselect = newSelectedOptions.filter(id => id !== optionId)
        newSelectedOptions = [optionId]
      } else {
        // Remove this option
        optionsToDeselect = [optionId]
        newSelectedOptions = newSelectedOptions.filter(id => id !== optionId)
      }
    } else {
      // For Multi Select options
      if (checked) {
        // First, remove any Single Select options
        const singleSelectOptionIds = question.options
          .filter(opt => opt.metaData?.buttonType === "Single Select")
          .map(opt => opt.optionId)
        
        optionsToDeselect = newSelectedOptions.filter(id => singleSelectOptionIds.includes(id))
        newSelectedOptions = newSelectedOptions.filter(id => !singleSelectOptionIds.includes(id))
        
        // Then add this option if not already present
        if (!newSelectedOptions.includes(optionId)) {
          newSelectedOptions.push(optionId)
        }
      } else {
        optionsToDeselect = [optionId]
        newSelectedOptions = newSelectedOptions.filter(id => id !== optionId)
      }
    }

    // Clear target questions for all deselected options
    optionsToDeselect.forEach(deselectedOptionId => {
      const deselectedOption = question.options.find(opt => opt.optionId === deselectedOptionId)
      if (deselectedOption && deselectedOption.targetQuestions) {
        clearNestedTargetQuestions(deselectedOption.targetQuestions)
      }
    })

    const newAnswer = {
      ...localAnswer,
      selectedOptions: newSelectedOptions,
      answer: null
    }
    handleAnswerChange(newAnswer)
  }

  const handleTextInput = (e) => {
    const value = e.target.value
    const newAnswer = {
      ...localAnswer,
      answer: value,
      selectedOptions: localAnswer.selectedOptions || []
    }
    setLocalAnswer(newAnswer)
  }

  const handleTextInputBlur = (e) => {
    const value = e.target.value
    const newAnswer = {
      ...localAnswer,
      answer: value,
      selectedOptions: localAnswer.selectedOptions || []
    }
    handleAnswerChange(newAnswer)
  }

  const renderTargetQuestions = (option) => {
    if (!option.targetQuestions || option.targetQuestions.length === 0) {
      return null
    }
 
    const isSelected = localAnswer.selectedOptions?.includes(option.optionId)
    if (!isSelected) {
      return null
    }
    
    // Check if all target questions have no selected options or empty answers
    const hasAnyAnswers = option.targetQuestions.some(
      targetQuestion =>
        (targetQuestion.selectedOptions && targetQuestion.selectedOptions.length > 0) ||
        (targetQuestion.answer && targetQuestion.answer.trim() !== '')
    );

    // If no target questions have answers, return null
    if (readOnly && !hasAnyAnswers) {
      return null
    }

    return (
      <div className={cn("ml-6 mt-4", nestingLevel > 0 && "ml-4")}>
        <div className={cn("bg-[#F5F3F0]", !nestingLevel && "border border-[#E3DED3] p-4 rounded-lg")}>
          <div className="space-y-4">
            {option.targetQuestions.map(targetQuestion => (
              <QuestionRenderer
                key={targetQuestion.questionId}
                question={targetQuestion}
                answers={answers}
                onAnswerChange={onAnswerChange}
                readOnly={readOnly}
                nestingLevel={nestingLevel + 1}
                demographicErrorMessages={demographicErrorMessages}
              />
            ))}
          </div>
        </div>
      </div>
    )
  }

  const renderSingleSelect = () => {
    if (readOnly) {
      // In readOnly mode, only show selected options
      const selectedOptions = question.options.filter(option => 
        localAnswer.selectedOptions?.includes(option.optionId)
      )
      
      if (selectedOptions.length === 0) {
        return null
      }

      return (
        <div className="space-y-4">
          <RadioGroup
            value={localAnswer.selectedOptions?.[0]?.toString() || ""}
            disabled={true}
          >
            {selectedOptions.map(option => (
              <div key={option.optionId} className="space-y-4">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value={option.optionId.toString()}
                    id={`${question.questionId}-${option.optionId}`}
                    disabled={true}
                  />
                  <label
                    htmlFor={`${question.questionId}-${option.optionId}`}
                    className="text-sm font-normal leading-none text-gray-700"
                  >
                    {option.optionText}
                  </label>
                </div>
                {renderTargetQuestions(option)}
              </div>
            ))}
          </RadioGroup>
        </div>
      )
    }

    return (
      <div className="space-y-4">
        <RadioGroup
          value={localAnswer.selectedOptions?.[0]?.toString() || ""}
          onValueChange={(value) => handleSingleSelect(parseInt(value))}
          disabled={readOnly}
          className="flex flex-wrap gap-4"
        >
          {question.options.map(option => (
            <div key={option.optionId} className="flex items-center space-x-2">
              <RadioGroupItem
                value={option.optionId.toString()}
                id={`${question.questionId}-${option.optionId}`}
                disabled={readOnly}
              />
              <label
                htmlFor={`${question.questionId}-${option.optionId}`}
                className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-gray-700"
              >
                {option.optionText}
              </label>
            </div>
          ))}
        </RadioGroup>
        {/* Render target questions for selected option */}
        {question.options.map(option => (
          <div key={option.optionId}>
            {renderTargetQuestions(option)}
          </div>
        ))}
      </div>
    )
  }

  const renderMultiSelect = () => {
    if (readOnly) {
      // In readOnly mode, only show selected options
      const selectedOptions = question.options.filter(option => 
        localAnswer.selectedOptions?.includes(option.optionId)
      )
      
      if (selectedOptions.length === 0) {
        return null
      }

      return (
        <div className="space-y-4">
          {selectedOptions.map(option => (
            <div key={option.optionId} className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={`${question.questionId}-${option.optionId}`}
                  checked={true}
                  disabled={true}
                />
                <label
                  htmlFor={`${question.questionId}-${option.optionId}`}
                  className="text-sm font-normal leading-none"
                >
                  {option.optionText}
                </label>
              </div>
              {renderTargetQuestions(option)}
            </div>
          ))}
        </div>
      )
    }

    return (
      <div className="space-y-4">
        {question.options.map(option => (
          <div key={option.optionId} className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id={`${question.questionId}-${option.optionId}`}
                checked={localAnswer.selectedOptions?.includes(option.optionId) || false}
                onCheckedChange={(checked) => handleMultiSelect(option.optionId, checked)}
                disabled={readOnly}
              />
              <label
                htmlFor={`${question.questionId}-${option.optionId}`}
                className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {option.optionText}
              </label>
            </div>
            {renderTargetQuestions(option)}
          </div>
        ))}
      </div>
    )
  }

  const renderMixedSelect = () => {
    if (readOnly) {
      // In readOnly mode, only show selected options
      const selectedOptions = question.options.filter(option => 
        localAnswer.selectedOptions?.includes(option.optionId)
      )
      
      if (selectedOptions.length === 0) {
        return null
      }

      // Separate options by type
      const multiSelectOptions = selectedOptions.filter(opt => opt.metaData?.buttonType === "Multi Select")
      const singleSelectOptions = selectedOptions.filter(opt => opt.metaData?.buttonType === "Single Select")

      return (
        <div className="space-y-4">
          {/* Render Multi Select options */}
          {multiSelectOptions.map(option => (
            <div key={option.optionId} className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={`${question.questionId}-${option.optionId}`}
                  checked={true}
                  disabled={true}
                />
                <label
                  htmlFor={`${question.questionId}-${option.optionId}`}
                  className="text-sm font-normal leading-none"
                >
                  {option.optionText}
                </label>
              </div>
              {renderTargetQuestions(option)}
            </div>
          ))}
          {/* Render Single Select options within RadioGroup */}
          {singleSelectOptions.length > 0 && (
            <RadioGroup
              value={localAnswer.selectedOptions?.find(id => {
                const option = question.options.find(opt => opt.optionId === id)
                return option?.metaData?.buttonType === "Single Select"
              })?.toString() || ""}
              disabled={true}
            >
              {singleSelectOptions.map(option => (
                <div key={option.optionId} className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value={option.optionId.toString()}
                      id={`${question.questionId}-${option.optionId}`}
                      disabled={true}
                    />
                    <label
                      htmlFor={`${question.questionId}-${option.optionId}`}
                      className="text-sm font-normal leading-none"
                    >
                      {option.optionText}
                    </label>
                  </div>
                  {renderTargetQuestions(option)}
                </div>
              ))}
            </RadioGroup>
          )}
        </div>
      )
    }

    // Get the selected Single Select option value for RadioGroup
    const selectedSingleSelectValue = localAnswer.selectedOptions?.find(id => {
      const option = question.options.find(opt => opt.optionId === id)
      return option?.metaData?.buttonType === "Single Select"
    })?.toString() || ""

    // Group options by type
    const multiSelectOptions = question.options.filter(opt => opt.metaData?.buttonType === "Multi Select")
    const singleSelectOptions = question.options.filter(opt => opt.metaData?.buttonType === "Single Select")

    return (
      <div className="space-y-4">
        {/* Render Multi Select options without RadioGroup */}
        {multiSelectOptions.map(option => {
          const isSelected = localAnswer.selectedOptions?.includes(option.optionId) || false
          return (
            <div key={option.optionId} className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={`${question.questionId}-${option.optionId}`}
                  checked={isSelected}
                  onCheckedChange={(checked) => handleMixedSelect(option.optionId, checked)}
                  disabled={readOnly}
                />
                <label
                  htmlFor={`${question.questionId}-${option.optionId}`}
                  className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {option.optionText}
                </label>
              </div>
              {renderTargetQuestions(option)}
            </div>
          )
        })}
        {/* Render Single Select options with RadioGroup */}
        {singleSelectOptions.length > 0 && (
          <RadioGroup
            value={selectedSingleSelectValue}
            onValueChange={(value) => handleMixedSelect(parseInt(value), true)}
            disabled={readOnly}
          >
            {singleSelectOptions.map(option => (
              <div key={option.optionId} className="space-y-4">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value={option.optionId.toString()}
                    id={`${question.questionId}-${option.optionId}`}
                    disabled={readOnly}
                  />
                  <label
                    htmlFor={`${question.questionId}-${option.optionId}`}
                    className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {option.optionText}
                  </label>
                </div>
                {renderTargetQuestions(option)}
              </div>
            ))}
          </RadioGroup>
        )}
      </div>
    )
  }

  const renderTextInput = () => {
    const textItem = {
      type: "text",
      placeholder: "Please specify...",
      pathToValue: `question-${question.questionId}`
    }

    return (
      <div className="max-w-md">
        <TextField
          item={textItem}
          value={localAnswer.answer || ""}
          onChange={handleTextInput}
          onBlur={handleTextInputBlur}
          disabled={readOnly}
          error={false}
          readOnly={readOnly}
        />
      </div>
    )
  }

  const renderSwitchButtons = () => {
    const toggleItem = {
      options: question.options.map(opt => ({
        value: opt.optionId,
        label: opt.optionText,
        disabled: opt.disabled
      }))
    }

    const handleToggleChange = (optionId) => {
      // Find the previously selected option to clear its target questions
      const previouslySelectedOptionId = localAnswer.selectedOptions?.[0]
      if (previouslySelectedOptionId && previouslySelectedOptionId !== optionId) {
        const previousOption = question.options.find(opt => opt.optionId === previouslySelectedOptionId)
        if (previousOption && previousOption.targetQuestions) {
          clearNestedTargetQuestions(previousOption.targetQuestions)
        }
      }

      const newAnswer = {
        ...localAnswer,
        selectedOptions: [optionId],
        answer: null
      }
      handleAnswerChange(newAnswer)
    }

    return (
      <SegmentedToggle
        item={toggleItem}
        value={localAnswer.selectedOptions?.[0]}
        disabled={readOnly}
        onValueChange={handleToggleChange}
      />
    )
  }
  const useSwitchButtons = question.answerType === "Single Select" && !question.isDemographicInformation;

  return (
    <div className="px-4 py-2">
      <div className="space-y-4">
        {useSwitchButtons ? (
          <>
            <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
              {question.question && (
                <label className={readOnly ? "text-xs font-normal text-neutral-7" : "text-sm font-medium"}>
                  {question.question}
                  {/* {question.isRequired && !readOnly && <span className="text-system-negative-2 ml-1">*</span>} */}
                </label>
              )}
              {renderSwitchButtons()}
            </div>
            {/* Render target questions for selected option below */}
            {question.options.map(option => (
              <div key={option.optionId}>
                {renderTargetQuestions(option)}
              </div>
            ))}
          </>
        ) : (
          <>
            {question.question && (
              <label className={readOnly ? "block text-xs font-normal text-neutral-7" : "block text-sm font-medium"}>
                {question.question}
                {/* {question.isRequired && !readOnly && <span className="text-system-negative-2 ml-1">*</span>} */}
              </label>
            )}
            <div className="space-y-4">
              {question.answerType === "Single Select" && renderSingleSelect()}
              {question.answerType === "Multi Select" && renderMultiSelect()}
              {question.answerType === "Mixed Select" && renderMixedSelect()}
              {question.answerType === "Text" && renderTextInput()}
            </div>
          </>
        )}
        {demographicErrorMessages && demographicErrorMessages[question.questionId] && (
          <div className="flex items-center mt-1 text-red-600 text-xs">
            <AlertTriangle className="h-3 w-3 mr-1" />
            <span className="mt-[1px]">{demographicErrorMessages[question.questionId]}</span>
          </div>
        )}
      </div>
    </div>
  )
}

QuestionRenderer.propTypes = {
  question: PropTypes.shape({
    questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    question: PropTypes.string.isRequired,
    answerType: PropTypes.oneOf(['Single Select', 'Multi Select', 'Mixed Select', 'Text', 'Single Segmented Toggle Select']).isRequired,
    isRequired: PropTypes.bool,
    isDemographicInformation: PropTypes.bool,
    answer: PropTypes.string,
    selectedOptions: PropTypes.arrayOf(PropTypes.number),
    options: PropTypes.arrayOf(PropTypes.shape({
      optionId: PropTypes.number.isRequired,
      optionText: PropTypes.string.isRequired,
      targetQuestions: PropTypes.array
    }))
  }).isRequired,
  answers: PropTypes.object.isRequired,
  onAnswerChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  nestingLevel: PropTypes.number,
  demographicErrorMessages: PropTypes.object
}

QuestionRenderer.defaultProps = {
  readOnly: false,
  nestingLevel: 0,
  demographicErrorMessages: {}
}

export { QuestionRenderer }